import { zodResolver } from "@hookform/resolvers/zod";
import UserValidator from "Validators/Common/User";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { HTTPErrorCallback } from "Helpers/Error";
import { cpfcnpjMask } from "Helpers/Format";
import { getPasswordSecurityVerifications } from "Helpers/Password";
import { clearMask } from "Helpers/Util";

import UseAuth from "Hooks/UseAuth";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";
import UseTwoFactorAuthentication from "Hooks/UseTwoFactorAuthentication";

import FormWrapper from "Components/Common/FormWrapper";
import Button from "Components/Global/Buttons";
import Checkbox from "Components/Global/Form/Checkbox";
import Input from "Components/Global/Form/Input";
import SafePassword from "Components/Global/SafePassword";

import DefaultForm from "./Components/DefaultForm";
import * as S from "./styles";

const SUBMIT_TIMEOUT_MS = 20000;

const Register = () => {
  const { storeToken } = UseAuth();
  const { setLoading } = UseGlobalLoading();
  const { showNotification } = UseNotification();
  const { api } = UseServices();
  const { runTwoFactorAuthentication } = UseTwoFactorAuthentication();

  const [password, setPassword] = useState("");

  const [document, setDocument] = useState("");

  const [communicationOptIn, setCommunicationOptIn] = useState(false);
  const [remmunerationOptIn, setRemmunerationOptIn] = useState(false);
  const [financialOptIn, setFinancialOptIn] = useState(false);
  const [optIn, setOptIn] = useState(false);

  const [token, setToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitTimeoutRef = useRef(null);

  const clearSubmitTimeout = () => {
    if (!submitTimeoutRef.current) return;

    clearTimeout(submitTimeoutRef.current);
    submitTimeoutRef.current = null;
  };

  const releaseSubmit = () => {
    clearSubmitTimeout();
    setIsSubmitting(false);
    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(
    () => () => {
      clearSubmitTimeout();
    },
    [],
  );

  const formSchema = z.object({
    full_name: UserValidator.full_name,
    email: UserValidator.email,
    phone: UserValidator.phone,
    responsible_name: UserValidator.responsible_name,
    document: UserValidator.document,
    password: UserValidator.password,
    confirm_password: UserValidator.confirm_password,
    communication_opt_in: UserValidator.communication_opt_in,
  });

  const methods = useForm({
    resolver: zodResolver(formSchema),
  });
  const { register } = methods;

  const validatePassword = (password, confirmPassword) => {
    const verifications = getPasswordSecurityVerifications(password);

    if (
      !verifications.hasEnoughLength ||
      !verifications.hasNumber ||
      !verifications.hasUppercase ||
      !verifications.hasSpecialCharacters
    ) {
      return {
        password_error:
          "A senha não atende aos requisitos mínimos de segurança!",
      };
    }

    if (password !== confirmPassword) {
      return {
        password_error: "A senha e a confirmação de senha não coincidem!",
      };
    }

    return { error: null };
  };

  const showError = (message) => {
    showNotification("danger", "Atenção!", message);
  };

  const submit = async (data) => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    submitTimeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
      setLoading(false);
      showNotification(
        "warning",
        "Atenção!",
        "O processamento está demorando mais que o esperado. Tente novamente.",
      );
    }, SUBMIT_TIMEOUT_MS);

    try {
      const { password_error } = validatePassword(
        data.password,
        data.confirm_password,
      );

      if (password_error) {
        releaseSubmit();
        return showError(password_error);
      }

      data.document = clearMask(data.document);
      data.phone = clearMask(data.phone);

      if (!remmunerationOptIn || !financialOptIn || !optIn) {
        releaseSubmit();
        return showError(
          "Você precisa aceitar todos os termos para se cadastrar na plataforma!",
        );
      }

      let newToken = token;

      if (!token) {
        setLoading(true);

        const response = await api.post("/users/opt-in", {
          ...data,
          opt_in: optIn,
          communication_opt_in: communicationOptIn,
          remmuneration_opt_in: remmunerationOptIn,
          financial_opt_in: financialOptIn,
        });

        const { token } = response.data;
        newToken = token;
        setToken(token);

        setLoading(false);
      }

      runTwoFactorAuthentication({
        route: "/users/confirm-opt-in",
        method: "post",
        token: newToken,
        data: {
          ...data,
        },
        cb: () => {
          storeToken(newToken, true);
          releaseSubmit();
        },
        errorCb: () => releaseSubmit(),
      });
    } catch (error) {
      releaseSubmit();
      HTTPErrorCallback(error);
    }
  };

  return (
    <S.Wrapper>
      <FormWrapper {...methods} submit={submit}>
        <S.Register>
          <DefaultForm name="Cadastro">
            <S.PersonalForm>
              <Input
                {...register("full_name")}
                label="Instituição:"
                placeholder="Digite aqui"
                gridArea="full_name"
              />
              <Input
                {...register("document")}
                label="CNPJ:"
                placeholder="Digite aqui"
                gridArea="document"
                value={document}
                onChange={(e) => {
                  setDocument(cpfcnpjMask(e.target.value));
                }}
              />
              <Input
                {...register("responsible_name")}
                label="Contato:"
                placeholder="Digite aqui"
                gridArea="responsible_name"
              />
              <Input
                {...register("email")}
                label="E-mail:"
                placeholder="Digite aqui"
                gridArea="email"
              />
              <Input
                {...register("phone")}
                label="Telefone:"
                placeholder="Digite aqui"
                mask="+99 (99) 99999-9999"
                gridArea="phone"
              />
              <Input
                {...register("password")}
                label="Senha:"
                placeholder="Digite aqui"
                type="password"
                gridArea="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Input
                {...register("confirm_password")}
                label="Repita a senha:"
                placeholder="Digite aqui"
                type="password"
                gridArea="confirm_password"
              />
            </S.PersonalForm>

            <S.CommunicationOptin>
              <SafePassword password={password} color="white" />

              <div />
            </S.CommunicationOptin>
          </DefaultForm>

          <S.Optins>
            <Checkbox
              {...register("communication_opt_in")}
              label="Desejo receber notificações de projetos e seletivas da Sevenx."
              inputChecked={communicationOptIn}
              onChange={(e) => setCommunicationOptIn(e.target.checked)}
            />

            <Checkbox
              {...register("remmuneration_opt_in")}
              label="Você está ciente que a contratação do serviço de captação de recursos significa que a Sevenx deverá ser remunerada em caso de sucesso?"
              inputChecked={remmunerationOptIn}
              onChange={(e) => setRemmunerationOptIn(e.target.checked)}
            />

            <Checkbox
              {...register("financial_opt_in")}
              label="Está ciente que o cadastro no sistema não garante aporte financeiro no seu projeto?"
              inputChecked={financialOptIn}
              onChange={(e) => setFinancialOptIn(e.target.checked)}
            />

            <Checkbox
              {...register("opt_in")}
              customLabel={
                <>
                  <p>
                    Concordo com os{" "}
                    <strong>
                      <a
                        href="/Termos-de-aceite-cadastrado-de-projeto-Sevenx.pdf"
                        target="_blank"
                        rel="noreferrer"
                      >
                        TERMO DE USO E POLÍTICA DE PRIVACIDADE - SEVENX
                        PRODUÇÕES ARTÍSTICAS
                      </a>
                    </strong>
                  </p>
                </>
              }
              inputChecked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
            />
          </S.Optins>

          <S.Buttons>
            <Button
              type="submit"
              text={isSubmitting ? "Processando..." : "Cadastrar"}
              disabled={isSubmitting}
            />
          </S.Buttons>
        </S.Register>
      </FormWrapper>
    </S.Wrapper>
  );
};

export default Register;
